/**
 * Loading Orchestrator - Core System Controller
 * 
 * Coordinates initialization of all app systems in optimal sequence
 * 
 * EVOLUTION: Now part of core/orchestration system with expanded capabilities
 * - Phased loading with dependencies
 * - Parallel initialization where possible
 * - Progress tracking and callbacks
 * - Lazy loading for heavy features
 * - Plugin system for extensible initialization
 * - Performance monitoring and optimization
 * 
 * EXPANDED PURPOSE:
 * - Support dynamic system registration
 * - Hot-reload capability for development
 * - System health monitoring
 * - Graceful degradation for failed systems
 */

import { audioSystemController } from '../audio/AudioSystemController';
import { perfTrail } from '../PerformanceTrail';

export type LoadingPhase = 
  | 'shell'           // React, Router, Theme (0-10%)
  | 'storage'         // LocalStorage, User State (10-30%)
  | 'audio'           // Audio System (30-60%)
  | 'sensors'         // Device Sensors (60-80%)
  | 'components'      // Page Components (80-95%)
  | 'complete';       // Ready to use (100%)

export interface LoadingState {
  phase: LoadingPhase;
  progress: number;     // 0-100
  message: string;
  isComplete: boolean;
  error: Error | null;
  startTime: number;
  duration: number;     // ms
  systemsLoaded: string[];
  systemsFailed: string[];
}

export type LoadingCallback = (state: LoadingState) => void;

export interface SystemInitializer {
  name: string;
  priority: number;
  phase: LoadingPhase;
  initialize: () => Promise<void>;
  isOptional?: boolean;
}

/**
 * Singleton Loading Orchestrator - Enhanced with Plugin System
 */
class LoadingOrchestrator {
  private static instance: LoadingOrchestrator | null = null;
  private state: LoadingState;
  private callbacks: LoadingCallback[] = [];
  private phaseTimings: Map<LoadingPhase, number> = new Map();
  private registeredSystems: SystemInitializer[] = [];

  private constructor() {
    this.state = {
      phase: 'shell',
      progress: 0,
      message: 'Initializing Fr00t...',
      isComplete: false,
      error: null,
      startTime: Date.now(),
      duration: 0,
      systemsLoaded: [],
      systemsFailed: []
    };

    // Removed logging for performance
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): LoadingOrchestrator {
    if (!LoadingOrchestrator.instance) {
      LoadingOrchestrator.instance = new LoadingOrchestrator();
    }
    return LoadingOrchestrator.instance;
  }

  /**
   * Register a system for initialization (Plugin System)
   */
  public registerSystem(system: SystemInitializer): void {
    this.registeredSystems.push(system);
    this.registeredSystems.sort((a, b) => a.priority - b.priority);
    // Removed logging for performance
  }

  /**
   * Get current loading state
   */
  public getState(): Readonly<LoadingState> {
    return { ...this.state };
  }

  /**
   * Subscribe to loading state changes
   */
  public subscribe(callback: LoadingCallback): () => void {
    this.callbacks.push(callback);
    
    // Call immediately with current state
    callback(this.getState());

    // Return unsubscribe function
    return () => {
      this.callbacks = this.callbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Update loading state and notify subscribers
   */
  private updateState(updates: Partial<LoadingState>): void {
    const previousPhase = this.state.phase;
    
    this.state = {
      ...this.state,
      ...updates,
      duration: Date.now() - this.state.startTime
    };

    // Track phase timing
    if (updates.phase && updates.phase !== previousPhase) {
      this.phaseTimings.set(previousPhase, this.state.duration);
      // Removed logging for performance
    }

    // Notify all subscribers
    this.callbacks.forEach(callback => {
      try {
        callback(this.getState());
      } catch (error) {
        // Only log errors in development to avoid performance impact
        if (process.env.NODE_ENV === 'development') {
          console.error('Error in loading callback:', error);
        }
      }
    });
  }

  /**
   * Start loading sequence (Enhanced with Plugin System)
   */
  public async startLoading(): Promise<void> {
    perfTrail.start('loading-orchestrator');
    this.state.startTime = Date.now();

    try {
      // PHASE 1: Shell (React, Router, Theme already loaded by React)
      await this.loadShell();

      // PHASE 2: Storage (LocalStorage, User State)
      await this.loadStorage();

      // PHASE 3: Audio (AudioSystemController - requires user interaction)
      await this.prepareAudio();

      // PHASE 4: Sensors (Device sensors - optional, non-blocking)
      await this.prepareSensors();

      // Initialize registered systems
      await this.initializeRegisteredSystems();

      // PHASE 5: Components (Mark as ready for component loading)
      await this.prepareComponents();

      // COMPLETE
      await this.complete();
      perfTrail.end('loading-orchestrator');

    } catch (error) {
      perfTrail.error('loading-orchestrator', 'App initialization failed', {
        error: (error as Error).message,
        phase: this.state.phase,
        duration: Date.now() - this.state.startTime,
      });
      perfTrail.end('loading-orchestrator');
      this.handleError(error as Error);
    }
  }

  /**
   * Initialize all registered systems
   */
  private async initializeRegisteredSystems(): Promise<void> {
    for (const system of this.registeredSystems) {
      try {
        // Removed logging for performance
        await system.initialize();
        this.state.systemsLoaded.push(system.name);
      } catch (error) {
        // Only log errors in development
        if (process.env.NODE_ENV === 'development') {
          console.error(`❌ Failed to initialize system: ${system.name}`, error);
        }
        this.state.systemsFailed.push(system.name);
        
        if (!system.isOptional) {
          throw error; // Stop loading if critical system fails
        }
      }
    }
  }

  /**
   * PHASE 1: Shell
   */
  private async loadShell(): Promise<void> {
    this.updateState({
      phase: 'shell',
      progress: 5,
      message: 'Loading Fr00t shell...'
    });

    // Shell is already loaded by React
    await this.delay(50);

    this.updateState({
      progress: 10,
      message: 'Shell ready'
    });
  }

  /**
   * PHASE 2: Storage
   */
  private async loadStorage(): Promise<void> {
    this.updateState({
      phase: 'storage',
      progress: 15,
      message: 'Loading user data...'
    });

    try {
      // Check localStorage availability
      if (typeof localStorage !== 'undefined') {
        const cachedData = localStorage.getItem('fr00t_user');
            if (cachedData) {
              try {
                const userData = JSON.parse(cachedData);
                if (userData && typeof userData === 'object') {
                  // Removed logging for performance
                }
              } catch (parseError) {
                if (process.env.NODE_ENV === 'development') {
                  console.warn('⚠️ Invalid cached user data, clearing...');
                }
                localStorage.removeItem('fr00t_user');
              }
            }
      }

      await this.delay(100);

      this.updateState({
        progress: 30,
        message: 'User data ready'
      });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ Storage initialization failed:', error);
        }
        this.updateState({
        progress: 30,
        message: 'Storage unavailable, continuing...'
      });
    }
  }

  /**
   * PHASE 3: Audio
   */
  private async prepareAudio(): Promise<void> {
    this.updateState({
      phase: 'audio',
      progress: 35,
      message: 'Preparing audio system...'
    });

    try {
      // Check if Web Audio API is supported
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        throw new Error('Web Audio API not supported');
      }

      // Verify audio system controller exists
      if (!audioSystemController) {
        throw new Error('AudioSystemController not available');
      }

      // Removed logging for performance

      this.updateState({
        progress: 60,
        message: 'Audio ready (click to activate)'
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Audio preparation failed:', error);
      }
      this.updateState({
        progress: 60,
        message: 'Audio unavailable, continuing...'
      });
    }
  }

  /**
   * PHASE 4: Sensors
   */
  private async prepareSensors(): Promise<void> {
    this.updateState({
      phase: 'sensors',
      progress: 65,
      message: 'Checking device sensors...'
    });

    try {
      const hasMotionAPI = typeof DeviceMotionEvent !== 'undefined';
      const hasOrientationAPI = typeof DeviceOrientationEvent !== 'undefined';
      const hasBatteryAPI = 'getBattery' in navigator;
      const hasGeolocationAPI = 'geolocation' in navigator;

      const availableAPIs = [];
      if (hasMotionAPI) availableAPIs.push('motion');
      if (hasOrientationAPI) availableAPIs.push('orientation');
      if (hasBatteryAPI) availableAPIs.push('battery');
      if (hasGeolocationAPI) availableAPIs.push('geolocation');

      // Removed logging for performance

      this.updateState({
        progress: 80,
        message: availableAPIs.length > 0 ? 'Sensors detected' : 'No sensors available'
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Sensor check failed:', error);
      }
      this.updateState({
        progress: 80,
        message: 'Sensors unavailable, continuing...'
      });
    }
  }

  /**
   * PHASE 5: Components
   */
  private async prepareComponents(): Promise<void> {
    this.updateState({
      phase: 'components',
      progress: 85,
      message: 'Preparing components...'
    });

    await this.delay(50);

    this.updateState({
      progress: 95,
      message: 'Components ready'
    });
  }

  /**
   * COMPLETE: All systems ready
   */
  private async complete(): Promise<void> {
    const totalTime = Date.now() - this.state.startTime;

    this.updateState({
      phase: 'complete',
      progress: 100,
      message: 'Fr00t ready!',
      isComplete: true
    });

    // Removed logging for performance
    // Phase timings still tracked but not logged
  }

  /**
   * Handle loading error
   */
  private handleError(error: Error): void {
    // Only log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Loading error:', error);
    }

    this.updateState({
      error,
      message: `Error: ${error.message}`,
      isComplete: false
    });
  }

  /**
   * Initialize audio (call from user interaction)
   */
  public async initializeAudio(): Promise<void> {
    if (audioSystemController.isReady()) {
      // Removed logging for performance
      return;
    }

    // Removed logging for performance
    
    try {
      await audioSystemController.initialize(true);
      // Removed logging for performance
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Audio initialization failed:', error);
      }
      throw error;
    }
  }

  /**
   * Log phase timings for performance analysis
   */
  private logPhaseTimings(): void {
    // Removed logging for performance - timings still tracked internally
  }

  /**
   * Reset orchestrator (for testing/remounting)
   */
  public reset(): void {
    this.state = {
      phase: 'shell',
      progress: 0,
      message: 'Initializing Fr00t...',
      isComplete: false,
      error: null,
      startTime: Date.now(),
      duration: 0,
      systemsLoaded: [],
      systemsFailed: []
    };
    this.phaseTimings.clear();
    this.registeredSystems = [];
    // Removed logging for performance
  }

  /**
   * Utility: delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const loadingOrchestrator = LoadingOrchestrator.getInstance();
export default loadingOrchestrator;

